import { z } from 'zod';
import { optionalString, requiredString, optionalNumber, optionalDate, requiredDate, coerceNumber } from './helpers';

export const CONTRACT_TYPES = ['permanent', 'fixed_term', 'apprenticeship', 'internship'] as const;
export const SALARY_MODES = ['monthly', 'hourly'] as const;
export const THIRTEENTH_SALARY_MODES = ['december', 'monthly_provision'] as const;

export const contractSchema = z.object({
  valid_from: requiredDate(),
  contract_type: z.enum(CONTRACT_TYPES),
  salary_mode: z.enum(SALARY_MODES),
  hire_date: requiredDate(),
  end_date: optionalDate(),
  probation_months: optionalNumber(),
  notice_period_months: optionalNumber(),
  activity_rate: coerceNumber().pipe(z.number().min(0).max(100)),
  weekly_hours: optionalNumber(),
  base_salary: z.object({
    amount: coerceNumber().pipe(z.number().min(0)),
    currency: z.string().default('CHF'),
  }),
  thirteenth_salary: z.object({
    enabled: z.boolean().default(false),
    mode: z.enum(THIRTEENTH_SALARY_MODES).nullable().optional().default(null),
  }).default({ enabled: false, mode: null }),
  vacation: z.object({
    days_per_year: optionalNumber(),
    rate_percent: optionalNumber(),
  }).default({}),
  workplace: z.object({
    address: z.object({
      street: optionalString(),
      number: optionalString(),
      complement: optionalString(),
      zip: optionalString(),
      city: optionalString(),
      country: z.string().default('CH'),
    }).nullable().optional().default(null),
    canton: requiredString(),
  }),
  cct: z.object({
    applicable: z.boolean().default(false),
    name: optionalString(),
  }).default({ applicable: false, name: null }),
}).superRefine((data, ctx) => {
  // Fixed-term requires end_date
  if (data.contract_type === 'fixed_term' && !data.end_date) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'endDateRequiredForFixedTerm',
      path: ['end_date'],
    });
  }

  // Monthly salary requires days_per_year
  if (data.salary_mode === 'monthly' && (data.vacation.days_per_year == null)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'vacationDaysRequired',
      path: ['vacation', 'days_per_year'],
    });
  }

  // Hourly salary requires rate_percent
  if (data.salary_mode === 'hourly' && (data.vacation.rate_percent == null)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'vacationRateRequired',
      path: ['vacation', 'rate_percent'],
    });
  }

  // Thirteenth salary enabled requires mode
  if (data.thirteenth_salary.enabled && !data.thirteenth_salary.mode) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'thirteenthSalaryModeRequired',
      path: ['thirteenth_salary', 'mode'],
    });
  }

  // end_date must be after hire_date
  if (data.end_date && data.hire_date) {
    if (new Date(data.end_date) <= new Date(data.hire_date)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'endDateAfterHireDate',
        path: ['end_date'],
      });
    }
  }
});

export type ContractFormData = z.input<typeof contractSchema>;
export type ContractPayload = z.output<typeof contractSchema>;
